import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetSinglePropertyAsGuestQuery } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { useDispatch } from 'react-redux';
import { logout } from 'redux/features/auth/authSlice';
import { clearClaimsDraft } from 'redux/features/claims/claimsSlice';
import { clearPlansDraft } from 'redux/features/plans/plansSlice';
import { ButtonGroup, Flex, Grid, Icon, Text } from '@chakra-ui/react';
import Button from 'common/components/button';
import { backgroundTypeEnum } from 'common/utils/enums';
import ListItem from 'common/components/list-item';
import {
  FaArrowDown,
  FaCheckToSlot,
  FaFileSignature,
  FaQuoteLeft,
  FaShieldHalved,
  FaStar,
  FaUser,
} from 'react-icons/fa6';
import { PropertyBadge } from 'common/components/property-badge';
import LogoImage from 'common/components/logo-image';
import { handleSmoothScroll } from 'common/hooks/handleSmoothScroll';


// Define a default structure for the property data to be used for the skeleton UI
const defaultPropertyData = {
  name: 'your community',
  brand: {
    bgColor: '#191923',
    backgroundType: 'dark',
  },
  propertyDefaults: {
    terminationPolicy: {
      noticePeriod: '...',
      buyoutMonths: '...',
    },
  },
};

const Section = ({
  children,
  className,
  style,
  id,
  innerClassName,
  header = false,
  signupLink = '/signup',
  backgroundHeroImage,
  propertyData,
}) => {
  const navigate = useNavigate();
  const [offsetY, setOffsetY] = useState(0);

  const handleScroll = () => {
    if (header) {
      setOffsetY(window.pageYOffset);
    }
  };

  useEffect(() => {
    if (header) {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [header]);
  return (
    <section
      className={`flex flex-col p-6 justify-stretch items-center gap-6 ${header && 'max-h-screen'} ${className}`}
      id={id}
    >
      {header && (
        <div className='w-full flex justify-between gap-8 items-center'>
          {propertyData?.brand?.logo ? (
            <img
              src={
                propertyData?.brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK && propertyData?.brand?.logoAlt
                  ? propertyData.brand.logoAlt
                  : propertyData.brand.logo
              }
              alt={`${propertyData?.name} logo`}
              className={`ml-2 h-auto w-auto max-w-[180px] max-h-[50px]`}
              style={propertyData?.brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK && !propertyData?.brand?.logoAlt ? {
                filter: 'brightness(0)'
              } : null}              
            />
          ) : (
            <LogoImage
              size='s'
            />
          )}
          <ButtonGroup>
            <Button
              title='FAQ'
              wFull={false}
              onClick={() => window.open('http://www.releaserent.com/faq/renters', '_blank')}
              theme='secondary'
              className='hidden sm:flex'
            />
            <Button
              title='Get a Free Quote'
              wFull={false}
              onClick={() => navigate(signupLink)}
              className='property-primary-button'
            />
          </ButtonGroup>
        </div>
      )}
      <div 
        style={style}
        className={`flex flex-col md:p-16 lg:p-20 w-full rounded-2xl items-center flex-grow relative overflow-hidden ${header ? 'justify-center p-12' : 'p-6'} ${innerClassName}`}
      >
        <div className={`flex flex-col gap-8 max-w-6xl w-full z-10`}>{children}</div>
        {header && (
          <img
            src={
              backgroundHeroImage || 'https://cios-api-images.s3.us-east-1.amazonaws.com/static/getty-images-67QeArZCFww-unsplash.jpg'
            }
            alt={`${propertyData?.name || 'ReLease'} background image`}
          className='w-full h-[140%] absolute top-[-20%] left-0 z-0 opacity-25 object-cover'
            style={{ transform: `translateY(${offsetY * 0.5}px)` }}
          />
        )}
      </div>      
    </section>
  );
};

const Testimonial = ({ title, quote, author, className }) => {
  return (
    <Flex className={`flex-col gap-4 p-6 border-gray-200 border rounded-md bg-white ${className} h-full`}>
      <Flex className='gap-1 justify-center'>
        {Array.from({ length: 5 }).map((_, i) => (
          <Icon key={i} as={FaStar} className='text-yellow-400' boxSize={5} />
        ))}
      </Flex>
      <Text className='text-xl font-display tracking-tight'>{title}</Text>
      <Text className='text-gray-500'>"{quote}"</Text>
      <Flex className='h-full items-end justify-center'>
        <Text className='text-base text-gray-500'>{author}</Text>
      </Flex>
    </Flex>
  );
};

const listParentVariants = {
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: 'easeOut' },
  },
};

const PropertyLanding = () => {
  const { propertyID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: propertyDataFromApi,
    isLoading,
    isError,
    // error, // error object can be used for more detailed error display
  } = useGetSinglePropertyAsGuestQuery(propertyID, {
    skip: !propertyID,
  });

  useEffect(() => {
    dispatch(logout());
    dispatch(clearClaimsDraft());
    dispatch(clearPlansDraft());
  }, [dispatch]);

  // Use API data when available, otherwise use default data for skeleton loading.
  let propertyData = defaultPropertyData;
  propertyData = isLoading ? defaultPropertyData : propertyDataFromApi;

  // Handle error state
  if (isError) {
    navigate('/signup');
  }

  // Set up customization based on property data (real or default)
  const isDarkBg =
    propertyData?.brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;
  const textColorClass = isDarkBg ? 'text-white' : 'text-black';
  const terminationPolicy = propertyData?.propertyDefaults?.terminationPolicy;
  const brand = propertyData?.brand;
  const primaryColor = brand?.primaryColor;
  const bgColor = brand?.bgColor;
  const buttonTextColor = brand?.buttonTextColor;

  useEffect(() => {
    const styleTagId = 'property-branding-style';

    // Manage style tag for font-family and button color overrides
    let styleTag = document.getElementById(styleTagId);
    const hasCustomStyles = primaryColor;

    if (hasCustomStyles) {
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = styleTagId;
        document.head.appendChild(styleTag);
      }
      let css = '';
      if (primaryColor) {
        const finalButtonTextColor = buttonTextColor || bgColor;
        css += `
          .property-primary-button {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
            ${finalButtonTextColor ? `color: ${finalButtonTextColor} !important;` : ''}
          }
          
          .property-primary-button:hover {
            background-color: ${brand?.bgColor} !important;
            border-color: ${brand?.bgColor} !important;
            color: ${primaryColor} !important;
          }
        `;
      }
      styleTag.innerHTML = css;
    } else if (styleTag) {
      document.head.removeChild(styleTag);
    }

    return () => {
      const styleTagToRemove = document.getElementById(styleTagId);
      if (styleTagToRemove) document.head.removeChild(styleTagToRemove);
    };
  }, [primaryColor, bgColor, buttonTextColor]);

  return (
    <div>
      <Section
        className={`${textColorClass} min-h-screen justify-center relative`}
        style={{ backgroundColor: brand?.bgColor }}
        header
        signupLink={`/properties/${propertyID}/signup`}
        backgroundHeroImage={brand?.backgroundHeroImage}
        propertyData={propertyData}
      >        
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
          className='text-4xl lg:text-5xl font-display tracking-tight max-w-4xl'
        >
          Get peace of mind with a flexible leasing option from ReLease <span className='hidden md:inline text-2xl tracking-normal font-normal'><br />Save thousands in fees and move out when you want to with ReLease</span>
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
        >
          <ButtonGroup>
            <Button
              title='Get a Free Quote'
              wFull={false}
              onClick={() => navigate(`/properties/${propertyID}/signup`)}
              className='property-primary-button'
              isLoading={isLoading}
            />
          </ButtonGroup>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.8 }}
          className='opacity-60 inline-flex items-center gap-4 hover:opacity-100 cursor-pointer transition-all absolute bottom-10 md:bottom-16 font-medium'
          onClick={() => handleSmoothScroll('info-start')}>
          <Icon as={FaArrowDown} />
          <span>
          Learn more about <span className='hidden md:inline'>a flexible leasing option from </span>ReLease
          </span>
        </motion.div>
      </Section>
      <Section id='info-start'>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className='flex flex-col items-center text-center gap-8'
        >
          <Flex className='flex-col gap-4 max-w-4xl items-center'>
            <PropertyBadge property={propertyData} className='mb-12' />
            <h2 className='text-4xl font-display tracking-tight mb-4 max-w-3xl'>
              Get a flexible leasing option from ReLease at {propertyData?.name}
            </h2>
            <Text className='max-w-3xl'>
              Life is hard to predict. That’s why {propertyData?.name} has partnered with ReLease to
              offer you the flexibility and peace of mind of a flexible lease.
            </Text>            
          </Flex>
          <ButtonGroup>
            <Button
              title='Get a Free Quote'
              wFull={false}
              onClick={() => navigate(`/properties/${propertyID}/signup`)}
              className='property-primary-button'
              isLoading={isLoading}
            />
          </ButtonGroup>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, amount: 0.2 }}
            variants={listParentVariants}
            className='grid w-full grid-cols-1 md:grid-cols-2 gap-8 mt-10'
          >
            <motion.div variants={listItemVariants}>
              <Flex className='flex-col gap-4 p-6 rounded-md border border-gray-200 bg-white h-full'>
                <h3 className='text-2xl font-display tracking-tight'>
                  Move On Your Own Terms
                </h3>
                <Text className='text-gray-500'>
                  You’re in control. Whether it’s an unexpected life change, a
                  can’t miss opportunity, or just because, you don’t need to wait
                  until your lease expires to make your next move.
                </Text>
              </Flex>
            </motion.div>
            <motion.div variants={listItemVariants}>
              <Flex className='flex-col gap-4 p-6 rounded-md border border-gray-200 bg-white h-full'>
                <h3 className='text-2xl font-display tracking-tight'>
                  Financial Protection
                </h3>
                <Text className='text-gray-500'>
                  ReLease saves you thousands of dollars if you need to move out
                  early. Without ReLease you’re responsible for the fees below.
                </Text>
              </Flex>
            </motion.div>
          </motion.div>
        </motion.div>
      </Section>
      <Section className='px-6 pb-20 flex items-center justify-center'>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className='bg-gray-50 rounded-2xl p-8 mt-8 flex flex-col gap-6 border border-gray-200 max-w-6xl'
        >
          <h2 className='text-3xl font-display tracking-tight'>
            What could you save with ReLease?
          </h2>
          <Grid className='grid-cols-1 md:grid-cols-2 gap-8'>
            <Flex className='flex-col gap-4 p-6 rounded-md border border-gray-200 md:col-span-2 bg-white'>
              <h3 className='text-2xl font-display tracking-tight'>
                Lease Break Terms at {propertyData?.name} without ReLease
              </h3>
              <Flex className='flex-col divide-y divide-gray-200'>
                <Flex className='flex-col md:flex-row justify-between md:items-center gap-4 py-4'>
                  <Text className='text-semibold'>
                    Notice Period
                    <span className='text-gray-500 block text-base'>
                      Minimum notice period, during which you'll still need to
                      pay for rent
                    </span>
                  </Text>
                  <Text>
                    {terminationPolicy?.noticePeriod || '30-60'}{' '}
                    <span className='text-gray-500'>days</span>
                  </Text>
                </Flex>
                <Flex className='flex-col md:flex-row justify-between md:items-center gap-4 py-4'>
                  <Text className='text-semibold'>
                    Early Termination Fee
                    <span className='text-gray-500 block text-base'>
                      Additional penalty you'll have to pay before you can move
                      out
                    </span>
                  </Text>
                  <Text>
                    {terminationPolicy?.buyoutMonths || '2-4'}{' '}
                    <span className='text-gray-500'>months of rent</span>
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Grid>
        </motion.div>
      </Section>
      <Section
        className={`${textColorClass}`}
        style={{ backgroundColor: brand?.bgColor }}
      >
        <Flex className='flex-col md:flex-row justify-between gap-5 md:gap-8 items-center text-center md:text-left'>
          <Flex className='flex-col gap-2 max-w-3xl px-2 md:px-0'>
            <h2 className='text-3xl font-display tracking-tight'>
              Already signed your lease?
            </h2>
            <Text className='opacity-60'>
              If you've already signed your lease or lease renewal at{' '}
              {propertyData?.name} you might still be eligible to purchase
              coverage. You must enroll within 7 days of your lease signing date
              to be eligible.
            </Text>
          </Flex>
          <ButtonGroup className='mb-1 md:mb-0'>
            <Button
              title='Purchase Now'
              wFull={false}
              onClick={() => navigate(`/properties/${propertyID}/signup`)}
              className='property-primary-button'
              isLoading={isLoading}
            />
          </ButtonGroup>
        </Flex>
      </Section>
      <Section>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className='flex flex-col items-center text-center gap-8'
        >
          <Flex className='flex-col gap-4'>
            <h2 className='text-3xl font-display tracking-tight'>
              Get covered in a few simple steps
            </h2>            
          </Flex>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, amount: 0.2 }}
            variants={listParentVariants}
            className='flex flex-col gap-6 py-6 px-6 border border-gray-200 bg-white rounded-md mt-4 w-full text-left'
          >
            <motion.div variants={listItemVariants}>
              <ListItem
                ListIcon={FaUser}
                title={<span className='text-xl'>Create Your ReLease Account</span>}
                description={
                  <span className='text-lg pt-0.5 block'>
                    Create a free ReLease member account online
                  </span>
                }
                line
              />
            </motion.div>
            <motion.div variants={listItemVariants}>
              <ListItem
                ListIcon={FaQuoteLeft}
                title={<span className='text-xl'>Get Free Quotes for the Rentals You're Touring</span>}
                description={
                  <span className='text-lg pt-0.5 block'>
                    Get quotes for the rental units you're interested in before signing your lease.
                  </span>
                }
                line
              />
            </motion.div>
            <motion.div variants={listItemVariants}>
              <ListItem
                ListIcon={FaFileSignature}
                title={<span className='text-xl'>Apply for the Rentals You're Interested In</span>}
                description={
                  <span className='text-lg pt-0.5 block'>
                    Apply for the rental units you're interested in and get the final details of your lease agreement.
                  </span>
                }
                line
              />
            </motion.div>
            <motion.div variants={listItemVariants}>
              <ListItem
                ListIcon={FaCheckToSlot}
                title={<span className='text-xl'>Purchase Coverage</span>}
                description={
                  <span className='text-lg pt-0.5 block'>
                    Purchase coverage within 7 days of your lease signing date
                  </span>
                }
              />
            </motion.div>
          </motion.div>
          <Flex className='flex-col gap-4'>
            <Text className='text-gray-500'>
              Still have questions?
            </Text>
            <ButtonGroup className='justify-center'>
              {/* <Button
                title='Get Started'
                wFull={false}
                onClick={() => navigate(`/properties/${propertyID}/signup`)}
                className='property-primary-button'
                isLoading={isLoading}
              /> */}
              <Button
                title='Visit Our FAQ'
                wFull={false}
                onClick={() => window.open('http://www.releaserent.com/faq/renters', '_blank')}
                theme='secondary'
              />
          </ButtonGroup>
          </Flex>          
        </motion.div>
      </Section>
      <Section innerClassName='bg-gray-50'>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className='flex flex-col items-center text-center gap-8'
        >
          <Flex className='flex-col gap-4'>
            <h2 className='text-3xl font-display tracking-tight'>
              Why renters love ReLease
            </h2>
            <Text className='text-gray-500'>
              Join thousands of renters saving thousands of dollars with
              ReLease.
            </Text>
          </Flex>
          <ButtonGroup>
            <Button
              title='Get a Free Quote'
              wFull={false}
              onClick={() => navigate(`/properties/${propertyID}/signup`)}
              className='property-primary-button'
              isLoading={isLoading}
            />
          </ButtonGroup>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, amount: 0.2 }}
            variants={listParentVariants}
            className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8'
          >
            <motion.div variants={listItemVariants}>
              <Testimonial
                title='Total confidence'
                quote='I had to relocate suddenly due to work, and ReLease was there to handle the transition. It was comforting to know I didn’t need to stress over my lease. The claim process was easy, and I could tell my landlord appreciated the hassle-free experience too.'
                author='Lucas B.'
              />
            </motion.div>
            <motion.div variants={listItemVariants}>
              <Testimonial
                title='Worry-free renting'
                quote='Using ReLease has completely changed my renting experience. Knowing that if I ever need to move unexpectedly, I’m covered, brings immense peace of mind. When I filed a claim, the process was seamless, and I felt fully supported throughout.'
                author='Michael S.'
                className='hidden md:flex'
              />
            </motion.div>
            <motion.div variants={listItemVariants}>
              <Testimonial
                title='No stress with new home'
                quote='When my partner and I found our dream home sooner than expected, we worried about being stuck with our lease. ReLease took all that stress away. Filing a claim was quick, and we were able to transition smoothly into homeownership without financial penalties.'
                author='Morgan R.'
                className='hidden lg:flex'
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </Section>
      <Section
        className={`${textColorClass}`}
        style={{ backgroundColor: brand?.bgColor }}
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className='w-full'
        >
          <Flex as='footer' className='flex-col gap-8 w-full'>
            <Grid className='grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 md:gap-4'>
              <Flex className='flex-col gap-4 sm:col-span-2 items-center sm:items-start mb-8'>
                <PropertyBadge property={propertyData} />
              </Flex>

              <Flex className='flex-col gap-4 items-center sm:items-start'>
                <Text className='font-semibold'>Support</Text>
                <a
                  href='https://www.releaserent.com/faq/renters'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='opacity-70 hover:opacity-100 transition-opacity'
                >
                  FAQ
                </a>
                <a
                  href='mailto:members@releaserent.com'
                  className='opacity-70 hover:opacity-100 transition-opacity'
                >
                  members@releaserent.com
                </a>
                <a
                  href='tel:701-515-9759'
                  className='opacity-70 hover:opacity-100 transition-opacity'
                >
                  (701) 515-9759
                </a>
              </Flex>

              <Flex className='flex-col gap-4 items-center sm:items-start'>
                <Text className='font-semibold'>Legal</Text>
                <a href='https://www.releaserent.com/legal/release-pre-lease-terms-conditions' target='_blank' rel='noopener noreferrer' className='opacity-70 hover:opacity-100 transition-opacity'>Pre-Lease Coverage Terms</a>
                <a href='https://www.releaserent.com/legal/release-mid-lease-terms-conditions' target='_blank' rel='noopener noreferrer' className='opacity-70 hover:opacity-100 transition-opacity'>Mid-Lease Coverage Terms</a>
                <a href='https://www.releaserent.com/legal/terms-of-service' target='_blank' rel='noopener noreferrer' className='opacity-70 hover:opacity-100 transition-opacity'>Terms of Service</a>
                <a href='https://www.releaserent.com/legal/privacy-policy' target='_blank' rel='noopener noreferrer' className='opacity-70 hover:opacity-100 transition-opacity'>Privacy Policy</a>
              </Flex>
            </Grid>
            <Flex
              className={`border-t pt-6 justify-center ${
                isDarkBg ? 'border-white/20' : 'border-black/10'
              }`}
            >
              <Text className='text-base opacity-70'>
                © 2024 ReLease Coverage Company Inc.
              </Text>
            </Flex>
          </Flex>
        </motion.div>
      </Section>
    </div>
  );
};
export default PropertyLanding;
